import { VoiceFieldBackdrop } from "@/components/fullscreen/VoiceFieldMic";
import type { VoiceNoteTurnState } from "@/hooks/useVoiceNoteTurn";
import { Loader2, Mic, SendHorizontal, X } from "lucide-react";

interface VoiceNoteComposerProps {
  state: VoiceNoteTurnState;
  elapsedSeconds: number;
  error: string | null;
  audioLevel?: number;
  available: boolean;
  onStart: () => void | Promise<void>;
  onCommit: () => void | Promise<void>;
  onCancel: () => void | Promise<void>;
  onReset: () => void | Promise<void>;
}

function formatDuration(seconds: number) {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safeSeconds / 60);
  return `${minutes}:${String(safeSeconds % 60).padStart(2, "0")}`;
}

export function VoiceNoteComposer({
  state,
  elapsedSeconds,
  error,
  audioLevel = 0,
  available,
  onStart,
  onCommit,
  onCancel,
  onReset,
}: VoiceNoteComposerProps) {
  if (state === "idle") {
    return (
      <button
        type="button"
        onClick={() => void onStart()}
        disabled={!available}
        className="group flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-orange-300/35 bg-orange-400/10 text-orange-200 transition-colors hover:border-orange-300/60 hover:bg-orange-400/20 disabled:cursor-not-allowed disabled:border-white/10 disabled:bg-white/[0.04] disabled:text-white/25"
        aria-label="Record an answer without interruptions"
        title={available ? "Record without interruptions" : "Your tutor is still getting ready"}
      >
        <Mic size={18} className="transition-transform group-hover:scale-105" />
      </button>
    );
  }

  if (state === "error") {
    return (
      <div
        className="flex min-w-0 flex-1 items-center gap-3 rounded-md border border-red-500/25 bg-red-500/10 px-3 py-2"
        role="alert"
      >
        <p className="min-w-0 flex-1 text-sm text-red-100/90">{error}</p>
        <button
          type="button"
          onClick={() => void onStart()}
          className="shrink-0 rounded-md bg-white/10 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-white/15"
        >
          Try again
        </button>
        <button
          type="button"
          onClick={() => void onReset()}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-white/55 transition-colors hover:bg-white/10 hover:text-white"
          aria-label="Dismiss voice-note error"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  if (state === "reviewing") {
    return (
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3 rounded-md border border-white/15 bg-[#16171C] px-3 py-2">
        <div className="min-w-0 basis-full sm:flex-1 sm:basis-auto" role="status">
          <p className="text-sm font-medium text-white">Maximum recording length reached</p>
          <p className="mt-1 text-xs text-white/65">
            {formatDuration(elapsedSeconds)} recorded. Not sent. Discarded after 30 seconds.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void onCancel()}
          className="min-h-11 rounded-md border border-white/15 px-3 text-sm font-semibold text-white/80 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-300"
        >
          Discard
        </button>
        <button
          type="button"
          onClick={() => void onCommit()}
          className="min-h-11 rounded-md bg-[#FD954F] px-3 text-sm font-semibold text-[#1C1C1E] hover:bg-[#F28747] focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
        >
          Send voice note
        </button>
      </div>
    );
  }

  const isRecording = state === "recording";
  const statusLabel =
    state === "starting"
      ? "Getting ready..."
      : state === "committing"
        ? "Sending..."
        : state === "cancelling"
          ? "Discarding..."
          : "Take your time. I'll wait until you send.";

  return (
    <div
      className="relative isolate flex min-w-0 flex-1 items-center gap-3 overflow-hidden rounded-md border border-red-400/25 bg-[#171318] px-3 py-2 shadow-[0_12px_32px_rgba(0,0,0,0.28)]"
      aria-live="polite"
    >
      <VoiceFieldBackdrop
        active={isRecording}
        pending={state === "starting"}
        audioLevel={isRecording ? audioLevel : 0}
      />

      <div className="relative z-10 flex min-w-0 flex-1 items-center gap-3">
        <span
          className="relative flex h-3 w-3 shrink-0 items-center justify-center"
          aria-hidden="true"
        >
          {isRecording && (
            <span className="absolute h-3 w-3 animate-ping rounded-full bg-red-400/30" />
          )}
          <span className="relative h-2 w-2 rounded-full bg-red-400" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm font-semibold tabular-nums text-white">
              {formatDuration(elapsedSeconds)}
            </span>
          </div>
          <p className="truncate text-xs text-white/48">{statusLabel}</p>
        </div>
      </div>

      <button
        type="button"
        onClick={() => void onCancel()}
        disabled={!isRecording}
        className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-white/10 bg-black/15 text-white/60 transition-colors hover:bg-white/[0.09] hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
        aria-label="Cancel voice note"
        title="Cancel"
      >
        <X size={17} />
      </button>

      <button
        type="button"
        onClick={() => void onCommit()}
        disabled={!isRecording}
        className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#FD954F] text-[#1C1C1E] transition-colors hover:bg-[#F28747] disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-white/35"
        aria-label="Send voice note"
        title="Send voice note"
      >
        {state === "committing" || state === "starting" ? (
          <Loader2 size={17} className="animate-spin" />
        ) : (
          <SendHorizontal size={17} />
        )}
      </button>
    </div>
  );
}
